<script setup>
// NOTICE: KAPTION-INTEGRATION
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import NaturalLanguageDateTimePicker from 'dashboard/components-next/DateTimePicker/NaturalLanguageDateTimePicker.vue';
import { toPlainId, getIdsForType } from './audienceHelpers';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  scheduledAt: {
    type: String,
    default: null,
  },
  inboxId: {
    type: [Number, String],
    default: null,
  },
  selectedAudience: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'update:title',
  'update:scheduledAt',
  'update:inboxId',
  'update:selectedAudience',
  'update:contactCount',
]);

const store = useStore();
const { t } = useI18n();

// Store getters
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const labels = useMapGetter('labels/getLabels');
const segments = useMapGetter('customViews/getContactCustomViews');

// Fetch contact segments on mount
onMounted(() => {
  store.dispatch('customViews/get', 'contact');
});

const internalTitle = computed({
  get: () => props.title,
  set: value => emit('update:title', value),
});

const internalScheduledAt = computed({
  get: () => props.scheduledAt,
  set: value => emit('update:scheduledAt', value),
});

const internalInboxId = computed({
  get: () => props.inboxId,
  set: value => emit('update:inboxId', value),
});

// NOTICE: KAPTION-INTEGRATION
// Split internal audience into label IDs and segment IDs for the two multi-selects.
// Uses toPlainId to unwrap nested {id:{id,type}} objects from Vue reactivity cycles.
const selectedLabelIds = computed({
  get: () => {
    const result = getIdsForType(props.selectedAudience, 'Label');
    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION:Step1] selectedLabelIds.get() props.selectedAudience:',
      JSON.stringify(props.selectedAudience),
      '=> result:',
      JSON.stringify(result)
    );
    return result;
  },
  set: ids => {
    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION:Step1] selectedLabelIds.set() raw ids from combobox:',
      JSON.stringify(ids)
    );
    const segmentEntries = props.selectedAudience
      .filter(a => a.type === 'Segment')
      .map(a => ({ id: toPlainId(a.id), type: 'Segment' }))
      .filter(a => a.id != null);
    const labelEntries = ids
      .map(id => ({ id: toPlainId(id), type: 'Label' }))
      .filter(a => a.id != null);
    const newAudience = [...labelEntries, ...segmentEntries];
    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION:Step1] selectedLabelIds.set() emitting:',
      JSON.stringify(newAudience)
    );
    emit('update:selectedAudience', newAudience);
  },
});

const selectedSegmentIds = computed({
  get: () => {
    const result = getIdsForType(props.selectedAudience, 'Segment');
    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION:Step1] selectedSegmentIds.get() props.selectedAudience:',
      JSON.stringify(props.selectedAudience),
      '=> result:',
      JSON.stringify(result)
    );
    return result;
  },
  set: ids => {
    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION:Step1] selectedSegmentIds.set() raw ids from combobox:',
      JSON.stringify(ids)
    );
    const labelEntries = props.selectedAudience
      .filter(a => a.type === 'Label')
      .map(a => ({ id: toPlainId(a.id), type: 'Label' }))
      .filter(a => a.id != null);
    const segmentEntries = ids
      .map(id => ({ id: toPlainId(id), type: 'Segment' }))
      .filter(a => a.id != null);
    const newAudience = [...labelEntries, ...segmentEntries];
    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION:Step1] selectedSegmentIds.set() emitting:',
      JSON.stringify(newAudience)
    );
    emit('update:selectedAudience', newAudience);
  },
});

const contactCount = ref(null);
const isLoadingCount = ref(false);

// Options
const inboxOptions = computed(
  () =>
    inboxes.value?.map(item => ({
      value: item.id,
      label: item.name,
    })) ?? []
);

const audienceList = computed(
  () =>
    labels.value?.map(item => ({
      value: item.id,
      label: item.title,
    })) ?? []
);

// NOTICE: KAPTION-INTEGRATION
// Segment options for multi-select
const segmentList = computed(
  () =>
    segments.value?.map(item => ({
      value: item.id,
      label: item.name,
    })) ?? []
);

// NOTICE: KAPTION-INTEGRATION
// Fetch contact count from both labels and segments (approximate, deduplicated)
const fetchContactCount = async () => {
  const hasLabels = selectedLabelIds.value.length > 0;
  const hasSegments = selectedSegmentIds.value.length > 0;

  if (!hasLabels && !hasSegments) {
    contactCount.value = null;
    emit('update:contactCount', null);
    return;
  }

  isLoadingCount.value = true;
  try {
    // Fetch label-based contact counts in parallel
    const labelPromises = hasLabels
      ? selectedLabelIds.value
          .map(labelId => labels.value.find(l => l.id === labelId))
          .filter(Boolean)
          .map(label => ContactAPI.get(1, 'name', label.title))
      : [];

    // Fetch segment-based contact counts in parallel
    const segmentPromises = hasSegments
      ? selectedSegmentIds.value
          .map(segmentId => segments.value.find(s => s.id === segmentId))
          .filter(s => s?.query)
          .map(segment => ContactAPI.filter(1, 'name', segment.query))
      : [];

    const responses = await Promise.all([...labelPromises, ...segmentPromises]);

    // Approximate count (may include duplicates across labels/segments)
    const totalCount = responses.reduce(
      (sum, r) => sum + (r.data?.meta?.count || 0),
      0
    );
    contactCount.value = totalCount;
    emit('update:contactCount', totalCount);
  } catch {
    contactCount.value = 0;
    emit('update:contactCount', 0);
  } finally {
    isLoadingCount.value = false;
  }
};

// Watch audience changes to fetch contact count
watch(
  () => props.selectedAudience,
  () => {
    fetchContactCount();
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Campaign Title -->
    <div class="flex flex-col gap-1">
      <Input
        v-model="internalTitle"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
        required
      />
    </div>

    <!-- Scheduled Time -->
    <NaturalLanguageDateTimePicker
      v-model="internalScheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      :placeholder="t('CAMPAIGN.DATETIME_PICKER.PLACEHOLDER')"
      required
    />

    <!-- Inbox Selection -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        v-model="internalInboxId"
        :options="inboxOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Audience Selection: Labels -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="selectedLabelIds"
        :options="audienceList"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION -->
    <!-- Audience Selection: Segments -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.SEGMENTS_LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="selectedSegmentIds"
        :options="segmentList"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.SEGMENTS_PLACEHOLDER')
        "
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Contact Count Info -->
    <div
      v-if="selectedLabelIds.length > 0 || selectedSegmentIds.length > 0"
      class="flex flex-col gap-2 text-sm"
    >
      <div
        v-if="isLoadingCount"
        class="animate-pulse h-4 w-32 bg-n-slate-3 rounded"
      />
      <div
        v-else-if="contactCount !== null && contactCount > 0"
        class="text-n-slate-11"
      >
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_1.CONTACTS_WILL_RECEIVE', {
            count: contactCount,
          })
        }}
        <span
          v-if="selectedLabelIds.length > 0 && selectedSegmentIds.length > 0"
          class="text-n-slate-10 text-xs"
        >
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.APPROX_NOTE') }}
        </span>
      </div>
      <div
        v-else-if="contactCount === 0"
        class="text-n-ruby-11 flex items-center gap-2"
      >
        <span class="i-lucide-alert-circle flex-shrink-0" />
        {{ t('CAMPAIGN.WHATSAPP.CREATE.BUILDER.STEP_1.NO_CONTACTS_FOUND') }}
      </div>
    </div>
  </div>
</template>
